import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const Loader = () => (
  <div className="min-h-screen bg-[#070707] grid place-items-center">
    <div className="w-8 h-8 rounded-full border-2 border-white/20 border-t-[#B87333] animate-spin" />
  </div>
);

// A deactivated user must never reach any protected surface.  All routes below
// funnel them into the formal /access-suspended notice.  Admins are exempt.
const isSuspended = (user) =>
  user && user !== false && user.role !== "admin" && user.status === "deactivated";

export function ProtectedRoute({ children, adminOnly = false }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <Loader />;
  if (user === false) return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  if (isSuspended(user)) return <Navigate to="/access-suspended" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/account" replace />;
  return children;
}

/**
 * Guard for the Executive Board (Founders Suite) Portal. Requires
 * `role === "director"` — sub-state (pending_payment / pending_authorization /
 * active / revoked) is handled inside the pages themselves so the user is
 * never redirected mid-payment.
 */
export function DirectorRoute({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <Loader />;
  if (user === false) return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  if (user.role === "admin") return children;
  if (user.role !== "director") return <Navigate to="/account" replace />;
  return children;
}

/**
 * Guard for pages that require a fully-approved account (Shop, Cart, Network).
 * Public visitors are still allowed to browse — only *logged-in* users with a
 * non-active status are redirected to the Verification Pending screen.
 */
export function ActiveOnlyRoute({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loader />;
  if (user === false) return children;                       // public browsing OK
  if (user.role === "admin") return children;                 // admins always pass
  if (isSuspended(user)) return <Navigate to="/access-suspended" replace />;
  if ((user.status || "pending_kyc") !== "active") {
    return <Navigate to="/verification-pending" replace />;
  }
  return children;
}
